import { COUPON_DURATION_LABELS, type CreateCouponInput } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { FormField } from "@/components/form-field";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errorMessage } from "@/lib/api";
import { billingApi, billingQueries, brl, shortDate } from "@/lib/billing-api";

export const Route = createFileRoute("/admin/cupons")({
  component: AdminCouponsPage,
});

const selectClass = "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm";

function AdminCouponsPage() {
  const coupons = useQuery(billingQueries.adminCoupons());
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"percent" | "amount">("percent");
  const [value, setValue] = useState("10");
  const [duration, setDuration] = useState<"once" | "repeating" | "forever">("once");
  const [months, setMonths] = useState("3");
  const [maxRedemptions, setMaxRedemptions] = useState("");

  const create = useMutation({
    mutationFn: () => {
      const input: CreateCouponInput = {
        code,
        name,
        percentOff: kind === "percent" ? Number(value) : null,
        amountOffCents: kind === "amount" ? Math.round(Number(value.replace(",", ".")) * 100) : null,
        duration,
        durationInMonths: duration === "repeating" ? Number(months) : null,
        maxRedemptions: maxRedemptions ? Number(maxRedemptions) : null,
      };
      return billingApi.createCoupon(input);
    },
    onSuccess: async (c) => {
      toast.success(`Cupom ${c.code} criado.`);
      setCode("");
      setName("");
      await queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const toggle = useMutation({
    mutationFn: (c: { id: string; active: boolean }) => billingApi.setCouponActive(c.id, c.active),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] }),
    onError: (err) => toast.error(errorMessage(err)),
  });
  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <>
      <PageHeader title="Cupons" description="No modo live o cupom é criado no Stripe (Coupon + Promotion Code); no simulado fica só no IAGENIX." />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Novo cupom</CardTitle>
            <CardDescription>O cliente digita o código no checkout, ou você aplica como desconto no detalhe do workspace.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <FormField id="cupom-codigo" label="Código">
                <Input id="cupom-codigo" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BEMVINDO" />
              </FormField>
              <FormField id="cupom-nome" label="Nome interno">
                <Input id="cupom-nome" value={name} onChange={(e) => setName(e.target.value)} placeholder="Boas-vindas 20%" />
              </FormField>
              <FormField id="cupom-tipo" label="Desconto">
                <div className="flex gap-2">
                  <select id="cupom-tipo" className={selectClass} value={kind} onChange={(e) => setKind(e.target.value === "amount" ? "amount" : "percent")}>
                    <option value="percent">%</option>
                    <option value="amount">R$</option>
                  </select>
                  <Input aria-label="Valor do desconto" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
                </div>
              </FormField>
              <FormField id="cupom-duracao" label="Duração">
                <select id="cupom-duracao" className={selectClass} value={duration} onChange={(e) => setDuration(e.target.value as typeof duration)}>
                  {Object.entries(COUPON_DURATION_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </FormField>
              {duration === "repeating" ? (
                <FormField id="cupom-meses" label="Meses">
                  <Input id="cupom-meses" inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value.replace(/\D/g, ""))} />
                </FormField>
              ) : null}
              <FormField id="cupom-max" label="Limite de usos (opcional)">
                <Input id="cupom-max" inputMode="numeric" value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value.replace(/\D/g, ""))} />
              </FormField>
              <div className="flex items-end">
                <Button type="submit" loading={create.isPending} disabled={code.length < 3 || name.length < 2}>
                  Criar cupom
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Cupons</CardTitle>
          </CardHeader>
          <CardContent>
            {coupons.isPending ? (
              <LoadingState rows={3} />
            ) : coupons.isError ? (
              <ErrorState error={coupons.error} onRetry={() => void coupons.refetch()} />
            ) : coupons.data.length === 0 ? (
              <EmptyState title="Nenhum cupom" description="Crie o primeiro acima." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead className="text-right">Usos</TableHead>
                    <TableHead>Criado</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons.data.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <span className="font-mono">{c.code}</span>
                        <p className="text-xs text-muted-foreground">{c.name}</p>
                      </TableCell>
                      <TableCell>{c.percentOff ? `${c.percentOff}%` : brl(c.amountOffCents)}</TableCell>
                      <TableCell>
                        {COUPON_DURATION_LABELS[c.duration]}
                        {c.duration === "repeating" ? ` (${c.durationInMonths} meses)` : ""}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {c.timesRedeemed}
                        {c.maxRedemptions ? ` / ${c.maxRedemptions}` : ""}
                      </TableCell>
                      <TableCell>{shortDate(c.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={c.active ? "success" : "secondary"} className="mr-2">
                          {c.active ? "Ativo" : "Inativo"}
                        </Badge>
                        <Button size="sm" variant="outline" onClick={() => toggle.mutate({ id: c.id, active: !c.active })}>
                          {c.active ? "Desativar" : "Ativar"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
